import { useRef, useState } from "react";
import { Mail, Lock, User, Phone } from "lucide-react";
import { Button, CircularProgress, Typography, Box } from "@mui/material";
import { LoginStyles } from "../Styles/loginStyles";
import { useTheme } from "@mui/material/styles";
import { TextFieldWithIcon } from "../Componets/ui/TextFieldIcon";
import { MotionBox } from "../Componets/ui/MotionBox";
import TermsAgreement from "./checkTerms";
import ReCAPTCHA from "react-google-recaptcha";
import { ApiConfig } from "@shared/Config/config";
import { AuthServices } from "../Services/authServices";
import { apiHandler } from "@shared/Functions/apiHandler";

export default function SignUp({
  isLoading,
  handleSignUpInputChange,
  signUpData,
  handleSignUpFunc, // parent expects handleSignUpFunc(e, captchaToken)
  error,
  marketingConsent,
  setMarketingConsent,
  termsAccpt,
  setTermsAccpt,
}) {
  const theme = useTheme();
  const classes = LoginStyles(theme);

  const recaptchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");

  const onRecaptchaChange = async (token) => {
    const payload = { token };
    const res = await AuthServices.verifyRecaptcha(payload);
    apiHandler(res, classes.successToast, true);
    if (res?.status === 200) {
      setCaptchaToken(token);
      setCaptchaError("");
    } else {
      setCaptchaError("Captcha expired. Please complete again.");
    }
  };

  const onRecaptchaExpired = () => {
    setCaptchaToken("");
    setCaptchaError("Captcha expired. Please complete again.");
  };

  const onSubmitClick = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      setCaptchaError("Please complete the captcha.");
      return;
    }
    await handleSignUpFunc(e, captchaToken);
    if (recaptchaRef.current && typeof recaptchaRef.current.reset === "function") {
      recaptchaRef.current.reset();
    }
  };

  return (
    <>
      <TextFieldWithIcon
        value={signUpData.fullName}
        handleLoginInputChange={handleSignUpInputChange}
        placeholder="Enter Your Full Name"
        icon={<User style={classes.inputIcon} />}
        title="fullName"
        delay={0.1}
        mt={2}
        error={error.fullName}
        helperText={error.fullName}
      />

      <TextFieldWithIcon
        value={signUpData.phoneNumber}
        handleLoginInputChange={handleSignUpInputChange}
        placeholder="Enter Your Phone Number"
        icon={<Phone style={classes.inputIcon} />}
        title="phoneNumber"
        delay={0.2}
        error={error.phoneNumber}
        helperText={error.phoneNumber}
      />

      <TextFieldWithIcon
        value={signUpData.email}
        handleLoginInputChange={handleSignUpInputChange}
        placeholder="Enter Your Email"
        icon={<Mail style={classes.inputIcon} />}
        title="email"
        delay={0.3}
        error={error.email}
        helperText={error.email}
      />

      <TextFieldWithIcon
        value={signUpData.password}
        handleLoginInputChange={handleSignUpInputChange}
        placeholder="Enter Your Password"
        icon={<Lock style={classes.inputIcon} />}
        title="password"
        delay={0.4}
        type="password"
        error={error.password}
        helperText={error.password}
      />

      <TextFieldWithIcon
        value={signUpData.confirmPassword}
        handleLoginInputChange={handleSignUpInputChange}
        placeholder="Enter Your Confirm Password"
        icon={<Lock style={classes.inputIcon} />}
        title="confirmPassword"
        delay={0.5}
        type="password"
        error={error.confirmPassword}
        helperText={error.confirmPassword}
      />

      <MotionBox delay={0.6}>
        <TermsAgreement
          marketingConsent={marketingConsent}
          setMarketingConsent={setMarketingConsent}
          termsAccpt={termsAccpt}
          setTermsAccpt={setTermsAccpt}
        />
      </MotionBox>

      <MotionBox delay={0.4} textAlign="left" mt={1}>
        <Box mt={2}>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={ApiConfig.recaptchaKey}
            onChange={onRecaptchaChange}
            onExpired={onRecaptchaExpired}
          />
          {(captchaError || error.captcha) && (
            <Typography color="error" variant="caption">
              {captchaError || error.captcha}
            </Typography>
          )}
        </Box>
      </MotionBox>

      <MotionBox delay={0.7}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          sx={classes.submitBtn}
          disabled={isLoading || !captchaToken}
          onClick={onSubmitClick}
        >
          {isLoading ? <CircularProgress size={24} /> : "Sign Up"}
        </Button>
      </MotionBox>
    </>
  );
}
